import React from "react"
import { fromJS } from "immutable"
import { notifications } from "@mantine/notifications"

// components
import { NewListTypeSelect } from "app/static/frontend/selects/containers/EnumSelect"
import { Stack, Text, Icon } from "@/components"

// action types
import * as actionDicts from "app/static/frontend/dashboards/action-types"

// helpers
import { isColor } from "app/static/frontend/imports/styleHelperFunctions"
import { sendUserActionPost } from "shared/user-actions/user-actions-action-creators"
import { prepareWidgetForSubmit } from "dashboards/helper-functions"

// constants
import { QuorumBlue, SaturatedColors, CompletedGreen } from "app/static/frontend/design-constants"

/**
 * @function
 * onSubmitWidgetEditForm for the StyledWidgetEditForm in app/static/frontend/dashboards/components/WidgetEditForm.jsx
 * This invokes the widget endpoint on the DashboardResource to create, update, or delete a widget schema.
 *
 * @param {Immutable} values => the WidgetEditForm values from the redux store
 * @param {Object} props => the WidgetEditForm in app/static/frontend/dashboards/containers/WidgetEditForm
 * @param {Object} props {Function} change - the change prop from the redux-form (updates the field value in the redux store)
 * @param {Object} props {Function} destroy - the destroy prop from the redux-form (clears the redux-form)
 * @param {Object} props {Function} destroyDashboardStore - action creator that clears the dashboard store
 * @param {Object} props {int} dashboardId - the id of the Dashboard object
 * @param {Object} props {Function} segueToUrl - global action creator that segues to an arbitrary Quorum URI
 * @param {Object} props {Function} sendAction - DjangIO action creator that sends an arbitrary API request
 * @param {Object} props {Object} widget - an object containing the widget schema
 * @param {Object} props {string} widgetKey - the slug of the widget schema in the Dashboard
 */
export const onSubmitWidgetEditForm = async (values, dispatch, props) => {
    // we disable the submit button while the sendAction request is resolving
    props.change("is_submitting", true)

    // if we have a valid widget in the form
    if (props.widget) {
        // save its value to the Dashboard
        await props.sendAction(
            DjangIO.app.widgets.models.Dashboard.objects.get(props.dashboardId),
            props.widget.meta.id ? actionDicts.patchWidgetActionDict : actionDicts.postWidgetActionDict,
            {
                method: props.widget.meta.id ? "patch" : "post",
                // name of @action method defined on the Resource
                action: "widget",
                // this is how we append an extra id to the end of the URI
                // see actionurls in tastypie_actions/actions
                actionPk: props.widgetKey,
                // schema
                kwargs: prepareWidgetForSubmit(props.widget),
                // redux payload
                payload: {},
            },
        )

        // if the widget does not already exist, i.e., we are POSTing, do not clear its cache
        if (props.widget.meta.id) {
            await props.sendAction(
                DjangIO.app.widgets.models.Dashboard.objects.get(props.dashboardId),
                actionDicts.clearDashboardWidgetCacheActionDict,
                {
                    // we are modifying the Dashboard cache, so let's use patch
                    method: "patch",
                    // name of @action method defined on the Resource
                    action: "clear_widget_cache",
                    // this is how we append an extra id to the end of the URI
                    // see actionurls in tastypie_actions/actions
                    actionPk: props.widgetKey,
                    // schema
                    kwargs: prepareWidgetForSubmit(props.widget),
                    // redux payload
                    payload: {},
                },
            )
        }
    }

    // If this is a new Widget, fire a UserAction
    if (!props.widget.meta.id) {
        sendUserActionPost({
            userActionType: DjangIO.app.useractions.types.UserActionType.created_dashboard_widget,
            quorumDataType: props.data_type,
        })
    }
    // since we've updated a widget, we want to reload the Dashboard
    // when we segue back to it
    // to do that, we just clear the store
    props.destroyDashboardStore({
        id: props.dashboardId,
    })

    // re-enable the submit button
    props.change("is_submitting", false)

    // segue back to the Dashboard.jsx
    props.segueToProfile(
        {
            id: props.dashboardId,
            extraUrlParameters: "?editing=true",
        },
        DjangIO.app.models.QuorumDataType.dashboard.value,
    )

    // when we save the edited widget to the Dashboard,
    // we want to ensure that we destroy the redux-form
    // this is because the same form loads different widgets,
    // and we don't want the SafedSearch (or other fields)
    // from this widget to get loaded into another widget
    props.destroy()
}

export const onSubmitSuccessWidgetEditForm = (result, dispatch, props) => {
    notifications.show({
        id: "saved-widget",
        title: "Saved Widget",
        message: (
            <Stack align="flex-start" spacing="md">
                <Text>Widget saved successfully</Text>
            </Stack>
        ),
        autoClose: false,
        color: "green.0",
        icon: <Icon icon="check" color={CompletedGreen} />,
        closeButtonProps: { "aria-label": "Dismiss notification" },
    })
}

export const onSubmitFailWidgetEditForm = (result, dispatch, error, props) => {
    // console.log("fail :(")
}

// informed by https://redux-form.com/8.3.0/examples/fieldarrays/
// validate.js for FieldArray implementation
export const validateWidgetEditForm = (immutableValues, props) => {
    const errors = {}

    // matches Colors schema in app/widgets/types.py
    const validateColors = () => {
        const colors = immutableValues.get("colors")
        const colorsErrors = {}
        const colorsArrayErrors = []

        if (colors) {
            colors.forEach((color, index) => {
                const colorKey = color.get("colorKey")
                const colorValue = color.get("colorValue")

                // ensure that the colorKey is a valid int that is >= 0
                if (
                    colorKey &&
                    // "0", "1", "2"
                    ((!colorKey.includes(".") && !(parseInt(colorKey.replace(",", "")) >= 0)) ||
                        // "0.2", ".5", ".75"
                        (colorKey.includes(".") && !(parseFloat(colorKey.replace(",", "")) >= 0)))
                ) {
                    colorsErrors.colorKey = "Condition must be a number that is >= 0."
                    colorsArrayErrors[index] = colorsErrors
                }

                // ensure that the colorValue is a valid CSS3 color
                // ("red", "ff0000", "rgb(255, 0, 0)", etc.)
                if (!isColor(colorValue)) {
                    colorsErrors.colorValue = "Value must be a valid CSS3 color value."
                    colorsArrayErrors[index] = colorsErrors
                }
            })
        }

        return colorsArrayErrors
    }

    const validateColorsHeatMap = () => {
        const colors = immutableValues.get("colors")
        const colorsErrors = {}
        const colorsArrayErrors = []

        if (
            // only perform check for heat maps since fully saturated colors are valid in
            // FrequencyTupleFields.point and FrequencyTupleFields.custom Maps
            props.frequencyTupleField === DjangIO.app.widgets.types.FrequencyTupleFields.heat.value &&
            SaturatedColors.includes(colors.first().get("colorValue"))
        ) {
            colorsErrors.colorValue = `Heat Map CSS3 color value must not be fully saturated (${SaturatedColors.join(
                ", ",
            )}).`
            colorsArrayErrors.push(colorsErrors)
        }

        return colorsArrayErrors
    }

    const validateNumberWidget = () => {
        const colorsErrors = validateColors()

        if (colorsErrors.length) {
            errors.colors = colorsErrors
        }
    }

    const validateQuicklinkWidget = () => {
        if (!immutableValues.get("quicklinkType")) {
            errors.quicklinkType = "Must have a quicklink selected"
        }
    }

    const validateVisualizationWidgetMap = () => {
        const mapVisualizationHeatQuantizedColorStep = immutableValues.get(
            "map_visualization_heat_quantized_color_step",
        )
        const colorsErrors = validateColors()
        const colorsErrorsHeatMap = validateColorsHeatMap()

        const mapVisualizationCustomColors = immutableValues.get("map_visualization_custom_colors")
        const mapVisualizationCustomColorsKey = immutableValues.get("map_visualization_custom_colors_key")
        const mapVisualizationCustomColor = mapVisualizationCustomColors.get(mapVisualizationCustomColorsKey)
        const mapVisualizationCustomColorsErrors = {}

        // ensure that the
        // mapVisualizationCustomColors[mapVisualizationCustomColorsKey]
        // is a valid CSS3 color
        // ("red", "ff0000", "rgb(255, 0, 0)", etc.)
        if (
            // if we have not actually selected a color value yet,
            // do not check to see if it's a valid css3 color value
            mapVisualizationCustomColor &&
            !isColor(mapVisualizationCustomColor)
        ) {
            mapVisualizationCustomColorsErrors[mapVisualizationCustomColorsKey] =
                "Value must be a valid CSS3 color value."
        }

        // "Each discrete scheme, such as d3.schemeBlues, is represented as an array of arrays of hexadecimal color strings.
        // The kth element of this array contains the color scheme of size k;
        // for example, d3.schemeBlues[9] contains an array of nine strings representing the nine colors of the blue sequential color scheme.
        // Sequential, single-hue color schemes support a size k ranging from 3 to 9."
        // https://github.com/d3/d3-scale-chromatic/#sequential-single-hue

        // Take https://github.com/d3/d3-scale-chromatic/blob/master/src/sequential-single/Blues.js, for example:
        // scheme[0] is translated into [#deebf7, #9ecae1, #3182bd].
        // scheme[6] is translated into [#f7fbff, #deebf7, #c6dbef, #9ecae1, #6baed6, #4292c6, #2171b5, #08519c, #08306b].

        // This means that, since mapVisualizationHeatQuantizedColorStep is shared between all possible default d3-scale-chromatic color schemes/scales,
        // the absolute minimum and maximum possible quantized color step (i.e., the color scheme range) range is between [3, 9].
        // Going any lower will result in a syntax error and render all colors in the map as #000000.
        if (
            parseInt(mapVisualizationHeatQuantizedColorStep) < 3 ||
            parseInt(mapVisualizationHeatQuantizedColorStep) > 9
        ) {
            errors.map_visualization_heat_quantized_color_step = "Quantized Color Step must be between [3, 9]"
        }

        if (colorsErrors.length) {
            errors.colors = colorsErrors
        }

        if (colorsErrorsHeatMap.length) {
            errors.colors = colorsErrorsHeatMap
        }

        if (Object.values(mapVisualizationCustomColorsErrors).length) {
            errors.map_visualization_custom_colors = mapVisualizationCustomColorsErrors
        }
    }

    const validateVisualizationWidget = () => {
        switch (parseInt(props.visSubtype)) {
            case DjangIO.app.widgets.types.VisualizationSubtype.map.value:
                validateVisualizationWidgetMap()
                break
            default:
                break
        }
    }

    // we only want to pass the values that are explicitly needed for each Widget to the WidgetEditForm
    switch (parseInt(props.widgetType)) {
        case DjangIO.app.widgets.types.DashboardWidgetType.number_widget.value:
            validateNumberWidget()
            break
        case DjangIO.app.widgets.types.DashboardWidgetType.quicklink_widget.value:
            validateQuicklinkWidget()
            break
        case DjangIO.app.widgets.types.DashboardWidgetType.visualization_widget.value:
            validateVisualizationWidget()
            break
        default:
            break
    }

    return errors
}

/**
 * @function
 * submitSearchifyFunc for the SheetsSearchifyWrapper in app/static/frontend/dashboards/components/WidgetEditForm.jsx
 *
 * @param {Function} change - the change prop from the redux-form (updates the field value in the redux store)
 * @param {int} dataType - the QuorumDataType of the form (data_type in the redux-form)
 * @param {string} field - the field of the form to change
 * @param {Object} searchifyFilters - the filters returned from the submitted searchify
 */
export const widgetEditFormSubmitSearchifyFunc =
    ({ change, dataType, field, searchifyFilters }) =>
    () => {
        const safedSearch = fromJS({
            // TODO: remove this if we determine that it is definitely not needed
            // under any circumstances
            // count: searchifyFilters.searchifyCount,
            added_ids: searchifyFilters.addedIds,
            data_type: dataType,
            deleted_ids: searchifyFilters.deletedIds,
            filters: searchifyFilters.querysetList,
        })
        change(field, safedSearch)
    }

export const translateColors = ({
    colors,
    // DashboardWidgetType.number_widget
    requiresColorKey,
}) =>
    // convert
    // {
    //     0: "#1a1a1a"
    //     22: "2a2a2a"
    // }
    // back to
    // [
    //     {
    //         colorKey: "0",
    //         colorValue: "#1a1a1a",
    //     },
    //     {
    //         colorKey: "22",
    //         colorValue: "2a2a2a"
    //     },
    // ]
    // this is the inverse of the transformation that occurs in
    // selectWidgetEditFormWidget getColors() in app/static/frontend/dashboards/selectors
    colors
        ? {
              colors: Object.entries(colors).map((color) => ({
                  ...(requiresColorKey && { colorKey: color[0] }), // Number Widgets use the colorKey for the Conditional Colors color boundaries
                  // Visualization Widgets treat the index as the colorKey in the redux-form
                  colorValue: color[1],
              })),
          }
        : {}

/**
 * @function
 * initializeWidgetEditForm for the WidgetEditForm componentDidMount in app/static/frontend/dashboards/components/WidgetEditForm.jsx
 *
 * @param {Object} dashboardWidget - an object containing the dashboard widget schema
 * @param {bool} hasLoadedWidget - whether or not we have loaded the Widget contained in the form yet
 * @param {Func} initialize - redux-form initialize function for form defaults
 * @param {int} widgetType - the DashboardWidgetType enum integer
 */
export const initializeWidgetEditForm = ({
    dashboardWidget,
    hasLoadedWidget,
    initialize,
    initialPathname,
    widgetType,
}) => {
    const initializeListWidget = () => {
        // if we successfully loaded the list_widget dashboardWidget from the request,
        // save it to the redux-form store with sane defaults
        if (dashboardWidget) {
            const safedSearch = dashboardWidget.data_sources[0].safed_search
            initialize({
                data_type: safedSearch.data_type,
                description: dashboardWidget.meta.description,
                has_loaded_widget: true,
                safedSearches: [safedSearch],
                text: dashboardWidget.meta.text && dashboardWidget.meta.text.trim(),
                // default is declared here rather than in the WidgetEditForm container initialize
                // because we don't want to add an extra re-render to the form on mount
                text_toggle_type: Boolean(dashboardWidget.meta.text),
            })
        } else {
            const defaultListType = NewListTypeSelect.defaultProps.options[0].value

            initialize({
                data_type: defaultListType,
                description: "",
                has_loaded_widget: true,
                safedSearches: [
                    {
                        data_type: defaultListType,
                    },
                ],
                text: `New ${DjangIO.app.widgets.types.DashboardWidgetType.by_value(parseInt(widgetType)).label} Name`,
                text_toggle_type: true,
            })
        }
    }

    /**
     * Initializes the trending widget form, with either from values from the
     * redux-store or initial default values
     */
    const initializeTrendingWidget = () => {
        if (dashboardWidget) {
            const safedSearch = dashboardWidget.data_sources[0].safed_search

            initialize({
                data_type: DjangIO.app.models.QuorumDataType.media.value,
                description: dashboardWidget.meta.description,
                has_loaded_widget: true,
                interval: DjangIO.app.hubble.queryset.TrendingPeriodEnum.week.value,
                safedSearches: [safedSearch],
                text: dashboardWidget.meta.text && dashboardWidget.meta.text.trim(),
                text_toggle_type: Boolean(dashboardWidget.meta.text),
            })
        } else {
            initialize({
                data_type: DjangIO.app.models.QuorumDataType.media.value,
                description: "",
                has_loaded_widget: true,
                interval: DjangIO.app.hubble.queryset.TrendingPeriodEnum.week.value,
                safedSearches: [
                    {
                        data_type: DjangIO.app.models.QuorumDataType.media.value,
                    },
                ],
                text: `New ${DjangIO.app.widgets.types.DashboardWidgetType.trending_widget.label} Name`,
                text_toggle_type: true,
            })
        }
    }

    const initializeQuicklinkWidget = () => {
        if (dashboardWidget) {
            initialize({
                has_loaded_widget: true,
                quicklinkType: dashboardWidget.meta.quicklink.type,
                quicklinkUrl: dashboardWidget.meta.quicklink.url,
                text: dashboardWidget.meta.quicklink.text && dashboardWidget.meta.quicklink.text.trim(),
            })
        } else {
            initialize({
                has_loaded_widget: true,
                quicklinkType: null,
                quicklinkUrl: "",
                text: "Add Custom Quicklink URL",
            })
        }
    }

    const getNumberWidgetDisplayType = () => {
        if (dashboardWidget.data_sources[0].group_by && dashboardWidget.data_sources[0].group_by.aggregation_field) {
            return DjangIO.app.widgets.types.NumberWidgetDisplayType.sum
        }

        return (
            DjangIO.app.widgets.types.NumberWidgetDisplayType.items().find(
                (numDisplayType) => numDisplayType.operation === dashboardWidget.operation,
            ) || DjangIO.app.widgets.types.NumberWidgetDisplayType.count
        )
    }

    const initializeNumberWidget = () => {
        if (dashboardWidget) {
            const safedSearches = dashboardWidget.data_sources.map((dataSource) => dataSource.safed_search)
            const numberWidgetDisplayType = getNumberWidgetDisplayType()

            initialize({
                aggregation_field:
                    // NumberWidgetDisplayType.sum
                    numberWidgetDisplayType.value === DjangIO.app.widgets.types.NumberWidgetDisplayType.sum.value
                        ? dashboardWidget.data_sources[0].group_by &&
                          dashboardWidget.data_sources[0].group_by.aggregation_field
                        : undefined,
                ...translateColors({
                    colors: dashboardWidget.colors,
                    requiresColorKey: true,
                }),
                color_toggle_type:
                    // we should default to the "Conditional Colors" FieldArray option
                    // if we have previously saved multiple color conditions
                    Boolean(dashboardWidget.colors && Object.keys(dashboardWidget.colors).length > 1),
                data_type: safedSearches[0].data_type,
                description: dashboardWidget.meta.description,
                displayType: numberWidgetDisplayType.value,
                has_loaded_widget: true,
                labels: dashboardWidget.meta.labels || [],
                safedSearches,
                text: dashboardWidget.meta.text && dashboardWidget.meta.text.trim(),
                text_toggle_type: Boolean(dashboardWidget.meta.text),
            })
        } else {
            initialize({
                colors: [
                    {
                        colorValue: QuorumBlue,
                    },
                ],
                color_toggle_type: false,
                data_type: NewListTypeSelect.defaultProps.options[0].value,
                description: "",
                displayType: DjangIO.app.widgets.types.NumberWidgetDisplayType.count.value,
                has_loaded_widget: true,
                labels: [],
                safedSearches: [
                    {
                        data_type: NewListTypeSelect.defaultProps.options[0].value,
                    },
                ],
                text: "New Number Widget Name",
                text_toggle_type: true,
            })
        }
    }

    const initializeTextWidget = () => {
        if (dashboardWidget) {
            initialize({
                description: dashboardWidget.meta.description,
                has_loaded_widget: true,
                text: dashboardWidget.meta.text && dashboardWidget.meta.text.trim(),
                text_toggle_type: Boolean(dashboardWidget.meta.text),
            })
        } else {
            initialize({
                description: "",
                has_loaded_widget: true,
                text: "New Text Widget Name",
                text_toggle_type: true,
            })
        }
    }

    const initializeVisualizationWidget = () => {
        if (dashboardWidget) {
            const visualizationSubType = parseInt(dashboardWidget.meta.vis_subtype)
            const safedSearches = dashboardWidget.data_sources.map(
                (dataSource) =>
                    dataSource.safed_search ||
                    // VisualizationSubtype.map
                    // frequency_tuple_field == FrequencyTupleFields.custom
                    {},
            )
            const displayType = getNumberWidgetDisplayType()
            const visualizationWidgetDefaults = {
                ...translateColors({
                    colors: dashboardWidget.meta.colors,
                }),
                data_type: safedSearches[0].data_type,
                description: dashboardWidget.meta.description,
                has_loaded_widget: true,
                initial_pathname: initialPathname,
                labels: dashboardWidget.meta.labels,
                safedSearches,
                text: dashboardWidget.meta.text && dashboardWidget.meta.text.trim(),
                text_toggle_type: Boolean(dashboardWidget.meta.text),
                visSubtype: dashboardWidget.meta.vis_subtype,
            }
            const defaultBarVisualizationDateField = DjangIO.app.widgets.types.DateFields.items().filter((field) =>
                field.qdts.includes(visualizationWidgetDefaults.data_type),
            )

            switch (visualizationSubType) {
                case DjangIO.app.widgets.types.VisualizationSubtype.pie.value:
                    initialize({
                        ...visualizationWidgetDefaults,
                        aggregation_field:
                            // NumberWidgetDisplayType.sum
                            displayType.value === DjangIO.app.widgets.types.NumberWidgetDisplayType.sum.value
                                ? dashboardWidget.data_sources[0].group_by &&
                                  dashboardWidget.data_sources[0].group_by.aggregation_field
                                : undefined,
                        displayType:
                            displayType.value ||
                            // we need the default NumberWidgetDisplayType.count
                            // fallback since VisualizationSubtype.pie widgets
                            // that were created before we added sums will have a null displayType
                            DjangIO.app.widgets.types.NumberWidgetDisplayType.count.value,
                    })
                    break
                case DjangIO.app.widgets.types.VisualizationSubtype.line.value:
                    initialize({
                        ...visualizationWidgetDefaults,
                        date_field: dashboardWidget.data_sources[0].group_by.date_field,
                        interval: dashboardWidget.data_sources[0].group_by.interval,
                        visCumulative: dashboardWidget.meta.vis_options.cumulative,
                    })
                    break
                case DjangIO.app.widgets.types.VisualizationSubtype.bar.value:
                    initialize({
                        ...visualizationWidgetDefaults,
                        bar_visualization_direction: dashboardWidget.meta.vis_options.bar_visualization_direction,
                        bar_visualization_display_style:
                            dashboardWidget.meta.vis_options.bar_visualization_display_style,
                        count: dashboardWidget.data_sources[0].count,
                        date_field:
                            !dashboardWidget.data_sources[0].count &&
                            dashboardWidget.data_sources[0].group_by &&
                            (dashboardWidget.data_sources[0].group_by.date_field ||
                                // since we either invoke the frequency_tuple_field or date_field
                                // logic in the DataSourceEngine,
                                // selectVisWidget in app/static/frontend/dashboards/selectors
                                // does not save the date_field if we have not selected
                                // one of the FrequencyTupleFields.date_fields frequency_tuple_fields

                                // this is necessary because
                                // DataSourceEngine group in app/widgets/engine checks
                                // (frequency_tuple_field and not date_field)
                                // to perform the correct QuerySet evaluation

                                // this means that, if we have a frequency_tuple_field which is not
                                // contained in FrequencyTupleFields.date_fields
                                // (if it is not a "When" FrequencyTupleField),
                                // we need to re-instantiate the default date_field for the saved data_type
                                (defaultBarVisualizationDateField.length && defaultBarVisualizationDateField[0].value)),
                        frequency_tuple_field:
                            !dashboardWidget.data_sources[0].count &&
                            dashboardWidget.data_sources[0].group_by &&
                            dashboardWidget.data_sources[0].group_by.frequency_tuple_field,
                        visCumulative:
                            // since we either invoke the frequency_tuple_field
                            // or date_field (which uses visCumulative) logic in the DataSourceEngine,
                            // selectVisWidget in app/static/frontend/dashboards/selectors
                            // does not save the cumulative field if we have not selected
                            // one of the FrequencyTupleFields.date_fields frequency_tuple_fields
                            dashboardWidget.meta.vis_options.cumulative || false,
                    })
                    break
                case DjangIO.app.widgets.types.VisualizationSubtype.map.value:
                    initialize({
                        ...visualizationWidgetDefaults,
                        frequency_tuple_field:
                            dashboardWidget.data_sources[0].group_by &&
                            dashboardWidget.data_sources[0].group_by.frequency_tuple_field,
                        map_visualization_custom_colors:
                            dashboardWidget.meta.vis_options.map_visualization_custom_colors,
                        map_visualization_custom_colors_key:
                            dashboardWidget.meta.vis_options.map_visualization_custom_colors_key,
                        map_visualization_heat_color_scale:
                            dashboardWidget.meta.vis_options.map_visualization_heat_color_scale,
                        map_visualization_heat_color_type:
                            dashboardWidget.meta.vis_options.map_visualization_heat_color_type,
                        map_visualization_heat_interpolation_color_space:
                            dashboardWidget.meta.vis_options.map_visualization_heat_interpolation_color_space,
                        map_visualization_heat_interpolation_colors:
                            dashboardWidget.meta.vis_options.map_visualization_heat_interpolation_colors,
                        map_visualization_heat_quantized_color_step:
                            dashboardWidget.meta.vis_options.map_visualization_heat_quantized_color_step,
                        map_visualization_region: dashboardWidget.meta.vis_options.map_visualization_region,
                    })
                    break
                default:
                    initialize(visualizationWidgetDefaults)
            }
        } else {
            initialize({
                // defaults for each SafedSearch colors/labels are automatically
                // added in the Button onChange in BarFormSection/LineFormSection/PieFormSection
                colors: [
                    {
                        colorValue: QuorumBlue,
                    },
                ],
                // count field to track whether
                // we are grouping by FrequencyTupleFields (GroupByWidget) or none (CountVisualizationWidget)
                count: false,
                data_type: DjangIO.app.models.QuorumDataType.bill.value,
                description: "",
                has_loaded_widget: true,
                initial_pathname: initialPathname,
                labels: ["Data Selection 1"],
                safedSearches: [
                    {
                        added_ids: [],
                        data_type: DjangIO.app.models.QuorumDataType.bill.value,
                        deleted_ids: [],
                        filters: [],
                    },
                ],
                text: "New Visualization Widget Name",
                text_toggle_type: true,
                visSubtype: DjangIO.app.widgets.types.VisualizationSubtype.bar.value,

                // line and bar
                date_field: DjangIO.app.widgets.types.DateFields.items()
                    .sort(DjangIO.valueSort)
                    .filter((field) => field.qdts.includes(DjangIO.app.models.QuorumDataType.bill.value))[0].value,
                interval: DjangIO.app.widgets.types.LineGraphTimeIntervals.month.value,
                visCumulative: false,

                // bar
                bar_visualization_direction: DjangIO.app.widgets.types.BarVisualizationDirection.vertical.value,
                bar_visualization_display_style: DjangIO.app.widgets.types.BarVisualizationDisplayStyle.regular.value,
                frequency_tuple_field: DjangIO.app.widgets.types.FrequencyTupleFields.region_supported_items()
                    .filter((field) => field.qdts.includes(DjangIO.app.models.QuorumDataType.bill.value))
                    .sort(DjangIO.valueSort)[0].value,

                // pie
                displayType: DjangIO.app.widgets.types.NumberWidgetDisplayType.count.value,
            })
        }
    }

    const initializeFavoritesWidget = () => {
        if (dashboardWidget) {
            initialize({
                description: dashboardWidget.meta.description,
                has_loaded_widget: true,
                text: dashboardWidget.meta.text && dashboardWidget.meta.text.trim(),
                text_toggle_type: Boolean(dashboardWidget.meta.text),
            })
        } else {
            initialize({
                description: "",
                has_loaded_widget: true,
                text: "",
                text_toggle_type: false,
            })
        }
    }

    // if we have already loaded the widget into the store,
    // do not override the extant values
    // (we don't want to re-initialize the form if we have already loaded the Widget into the WidgetEditForm redux-form)
    if (!hasLoadedWidget) {
        switch (parseInt(widgetType)) {
            case DjangIO.app.widgets.types.DashboardWidgetType.list_widget.value:
            case DjangIO.app.widgets.types.DashboardWidgetType.calendar_widget.value:
                initializeListWidget()
                break
            case DjangIO.app.widgets.types.DashboardWidgetType.quicklink_widget.value:
                initializeQuicklinkWidget()
                break
            case DjangIO.app.widgets.types.DashboardWidgetType.number_widget.value:
                initializeNumberWidget()
                break
            case DjangIO.app.widgets.types.DashboardWidgetType.text_widget.value:
                initializeTextWidget()
                break
            case DjangIO.app.widgets.types.DashboardWidgetType.visualization_widget.value:
                initializeVisualizationWidget()
                break
            case DjangIO.app.widgets.types.DashboardWidgetType.favorites_widget.value:
            case DjangIO.app.widgets.types.DashboardWidgetType.recents_widget.value:
                initializeFavoritesWidget()
                break
            case DjangIO.app.widgets.types.DashboardWidgetType.trending_widget.value:
                initializeTrendingWidget()
                break
            default:
                break
        }
    }
}

/**
 * @function
 * resetVisualizationWidgetEditForm is used to reset the colors, labels, and safedSearches redux fields in
 * - app/static/frontend/dashboards/components/WidgetEditFormSidebars/VisWidgetSidebarForm
 * - app/static/frontend/dashboards/components/WidgetEditFormSidebars/VisualizationFormSections/BarFormSection
 * - app/static/frontend/dashboards/components/WidgetEditFormSidebars/VisualizationFormSections/LineFormSection
 * - app/static/frontend/dashboards/components/WidgetEditFormSidebars/VisualizationFormSections/PieFormSection.
 *
 * @param {Function} change - redux-form change(), which saves a value to the field
 */
export const resetVisualizationWidgetEditForm = ({ change }) => {
    change(
        "colors",
        fromJS([
            {
                colorValue: QuorumBlue,
            },
        ]),
    )
    change("labels", fromJS(["Data Selection 1"]))
    change(
        "safedSearches",
        fromJS([
            {
                added_ids: [],
                data_type: DjangIO.app.models.QuorumDataType.bill.value,
                deleted_ids: [],
                filters: [],
            },
        ]),
    )
}
